import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Upload, ArrowDownToLine, ArrowUpFromLine, History, Pencil, Trash2, AlertTriangle, Search } from "lucide-react";
import { api, errMsg, fmtEuro } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, Panel, GoldButton, GhostButton, Empty, Field, ProductImage, inputCls } from "@/components/common";
import { OrderDialog, OrdersList } from "@/components/SupplierOrder";
import { ClipboardList } from "lucide-react";

const blank = { name: "", brand: "", category: "", supplier: "", price: 0, quantity: 0, min_threshold: 3, photo_path: "", description: "" };
const TYPE_LABEL = { carico: "Carico", scarico: "Scarico", vendita: "Vendita" };

const ProductDialog = ({ state, setState, onSaved }) => {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const f = state?.f || blank;
  const set = (k) => (e) => setState({ ...state, f: { ...f, [k]: e.target.value } });
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd);
      setState({ ...state, f: { ...f, photo_path: data.path } });
    } catch (err) { toast.error(errMsg(err)); } finally { setUploading(false); }
  };
  const save = async () => {
    if (!f.name.trim()) return toast.error("Inserisci il nome del prodotto");
    const body = { ...Object.fromEntries(Object.keys(blank).map((k) => [k, f[k]])), price: Number(f.price) || 0, quantity: Number(f.quantity) || 0, min_threshold: Number(f.min_threshold) || 0 };
    try {
      if (state.id) await api.put(`/products/${state.id}`, body);
      else await api.post("/products", body);
      toast.success("Prodotto salvato");
      setState(null);
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={!!state} onOpenChange={(o) => !o && setState(null)}>
      <DialogContent className="bg-[#FDFBF7] border-[#E8E1D7] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
        <DialogHeader><DialogTitle className="font-serif text-3xl font-normal">{state?.id ? "Modifica prodotto" : "Nuovo prodotto"}</DialogTitle></DialogHeader>
        <div className="grid gap-5 sm:grid-cols-[180px_1fr]">
          <div>
            <ProductImage path={f.photo_path} className="aspect-square w-full rounded-2xl border border-[#E8E1D7]" />
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="product-photo-input" />
            <GhostButton onClick={() => fileRef.current.click()} disabled={uploading} className="mt-3 w-full" data-testid="product-photo-button"><Upload className="h-4 w-4" />{uploading ? "Caricamento…" : "Carica foto"}</GhostButton>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2"><Field label="Nome *"><input className={inputCls} value={f.name} onChange={set("name")} data-testid="product-name-input" /></Field></div>
            <Field label="Marca"><input className={inputCls} value={f.brand} onChange={set("brand")} data-testid="product-brand-input" /></Field>
            <Field label="Categoria"><input className={inputCls} value={f.category} onChange={set("category")} data-testid="product-category-input" /></Field>
            <div className="sm:col-span-2"><Field label="Fornitore"><input className={inputCls} value={f.supplier || ""} onChange={set("supplier")} data-testid="product-supplier-input" /></Field></div>
            <Field label="Prezzo vendita (€)"><input type="number" min="0" step="0.5" className={inputCls} value={f.price} onChange={set("price")} data-testid="product-price-input" /></Field>
            <Field label="Soglia minima"><input type="number" min="0" className={inputCls} value={f.min_threshold} onChange={set("min_threshold")} data-testid="product-threshold-input" /></Field>
            {!state?.id && <Field label="Quantità iniziale"><input type="number" min="0" className={inputCls} value={f.quantity} onChange={set("quantity")} data-testid="product-quantity-input" /></Field>}
            <div className="sm:col-span-2"><Field label="Descrizione"><textarea rows={2} className={inputCls} value={f.description} onChange={set("description")} data-testid="product-description-input" /></Field></div>
          </div>
        </div>
        <div className="flex justify-end"><GoldButton onClick={save} disabled={uploading} data-testid="product-save-button">Salva</GoldButton></div>
      </DialogContent>
    </Dialog>
  );
};

const MovementDialog = ({ state, setState, onSaved }) => {
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState("");
  useEffect(() => { setQty(1); setNote(""); }, [state]);
  const save = async () => {
    try {
      await api.post(`/products/${state.p.id}/movements`, { type: state.type, quantity: Number(qty), note });
      toast.success(state.type === "carico" ? "Carico registrato" : "Scarico registrato");
      setState(null);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!state} onOpenChange={(o) => !o && setState(null)}>
      <DialogContent className="bg-[#FDFBF7] border-[#E8E1D7] max-w-sm" data-testid="movement-dialog">
        <DialogHeader><DialogTitle className="font-serif text-3xl font-normal">{state?.type === "carico" ? "Carico" : "Scarico"} · {state?.p.name}</DialogTitle></DialogHeader>
        <div className="text-sm text-[#6E675F]">Giacenza attuale: <b className="text-[#121212]">{state?.p.quantity}</b></div>
        <Field label="Quantità"><input type="number" min="1" className={inputCls} value={qty} onChange={(e) => setQty(e.target.value)} data-testid="movement-qty-input" /></Field>
        <Field label="Nota"><input className={inputCls} placeholder={state?.type === "scarico" ? "Es. uso in cabina" : "Es. ordine fornitore"} value={note} onChange={(e) => setNote(e.target.value)} data-testid="movement-note-input" /></Field>
        <div className="flex justify-end"><GoldButton onClick={save} data-testid="movement-save-button">Conferma</GoldButton></div>
      </DialogContent>
    </Dialog>
  );
};

const MovementList = ({ items }) => (
  items.length === 0 ? <Empty testId="movements-empty">Nessun movimento registrato.</Empty> : (
    <ul className="divide-y divide-[#E8E1D7]">
      {items.map((m) => (
        <li key={m.id} className="flex items-center gap-4 py-3" data-testid={`movement-row-${m.id}`}>
          <ProductImage path={m.photo_path} className="h-10 w-10 rounded-lg" />
          <div className="flex-1 min-w-0">
            <div className="truncate text-sm font-medium">{m.product_name}</div>
            <div className="truncate text-xs text-[#6E675F]">{new Date(m.date).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}{m.client_name ? ` · ${m.client_name}` : ""}{m.note ? ` · ${m.note}` : ""}</div>
          </div>
          <span className={`text-xs rounded-full px-2.5 py-0.5 border ${m.type === "carico" ? "border-[#2D6A4F]/30 text-[#2D6A4F]" : "border-[#C5A059]/40 text-[#8A6A22]"}`}>{TYPE_LABEL[m.type]}</span>
          <div className="w-16 text-right font-serif text-lg">{m.type === "carico" ? "+" : "−"}{m.quantity}</div>
          <div className="hidden sm:block w-20 text-right text-xs text-[#9E958A]">giac. {m.stock_after}</div>
        </li>
      ))}
    </ul>
  )
);

const ProductCard = ({ p, onEdit, onMove, onHistory, onDelete }) => {
  const low = p.quantity <= p.min_threshold;
  const pct = Math.min(100, (p.quantity / Math.max(p.min_threshold * 3, 1)) * 100);
  return (
    <Panel className="overflow-hidden fade-up" data-testid={`product-card-${p.id}`}>
      <div className="relative">
        <ProductImage path={p.photo_path} className="aspect-[4/3] w-full" />
        {low && <span className="absolute left-3 top-3 flex items-center gap-1 rounded-full bg-[#121212] px-2.5 py-1 text-xs text-[#E9CF86] border border-[#C5A059]" data-testid={`product-low-badge-${p.id}`}><AlertTriangle className="h-3 w-3" />{p.quantity === 0 ? "Esaurito" : "In esaurimento"}</span>}
      </div>
      <div className="p-5">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#C5A059]">{[p.brand, p.category].filter(Boolean).join(" · ") || "Prodotto"}</div>
        <div className="mt-1 font-serif text-2xl text-[#121212] truncate">{p.name}</div>
        <div className="mt-3 flex items-end justify-between">
          <div><span className="font-serif text-3xl" data-testid={`product-qty-${p.id}`}>{p.quantity}</span><span className="text-xs text-[#9E958A]"> pz · soglia {p.min_threshold}</span></div>
          <div className="text-sm text-[#6E675F]">{fmtEuro(p.price)}</div>
        </div>
        <div className="mt-2 h-1.5 rounded-full bg-[#F0E9DF]"><div className={`h-full rounded-full ${low ? "bg-[#D97706]" : "bg-[#C5A059]"}`} style={{ width: `${pct}%` }} /></div>
        <div className="mt-4 flex flex-wrap gap-2">
          <GhostButton onClick={() => onMove("carico")} className="px-3 py-1.5" data-testid={`product-load-${p.id}`}><ArrowDownToLine className="h-3.5 w-3.5" />Carico</GhostButton>
          <GhostButton onClick={() => onMove("scarico")} className="px-3 py-1.5" data-testid={`product-unload-${p.id}`}><ArrowUpFromLine className="h-3.5 w-3.5" />Scarico</GhostButton>
          <div className="ml-auto flex gap-3 items-center">
            <button onClick={onHistory} className="text-[#6E675F] hover:text-[#C5A059]" title="Storico" data-testid={`product-history-${p.id}`}><History className="h-4 w-4" /></button>
            <button onClick={onEdit} className="text-[#6E675F] hover:text-[#C5A059]" title="Modifica" data-testid={`product-edit-${p.id}`}><Pencil className="h-4 w-4" /></button>
            <button onClick={onDelete} className="text-[#6E675F] hover:text-red-700" title="Elimina" data-testid={`product-delete-${p.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        </div>
      </div>
    </Panel>
  );
};

export default function Inventory() {
  const [products, setProducts] = useState([]);
  const [movements, setMovements] = useState([]);
  const [q, setQ] = useState("");
  const [onlyLow, setOnlyLow] = useState(false);
  const [pDlg, setPDlg] = useState(null);
  const [mDlg, setMDlg] = useState(null);
  const [hist, setHist] = useState(null);
  const [orders, setOrders] = useState([]);
  const [orderOpen, setOrderOpen] = useState(false);

  const load = useCallback(async () => {
    const [p, m, o] = await Promise.all([api.get("/products"), api.get("/movements"), api.get("/orders")]);
    setProducts(p.data);
    setMovements(m.data);
    setOrders(o.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openHistory = async (p) => {
    const { data } = await api.get("/movements", { params: { product_id: p.id } });
    setHist({ p, items: data });
  };
  const del = async (p) => { if (window.confirm(`Eliminare ${p.name}?`)) { await api.delete(`/products/${p.id}`); load(); } };
  const lowCount = products.filter((p) => p.quantity <= p.min_threshold).length;
  const shown = products.filter((p) => (!onlyLow || p.quantity <= p.min_threshold) && `${p.name} ${p.brand} ${p.category}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="inventory-page">
      <PageHeader overline="Magazzino" title="Prodotti e scorte">
        <GhostButton onClick={() => setOrderOpen(true)} data-testid="supplier-order-button"><ClipboardList className="h-4 w-4" /> Ordine fornitore</GhostButton>
        <GoldButton onClick={() => setPDlg({ f: { ...blank } })} data-testid="add-product-button"><Plus className="h-4 w-4" /> Nuovo prodotto</GoldButton>
      </PageHeader>
      <Tabs defaultValue="prodotti">
        <TabsList className="mb-6 bg-[#F6F1EA] p-1 rounded-full">
          <TabsTrigger value="prodotti" className="rounded-full px-4 data-[state=active]:bg-[#121212] data-[state=active]:text-[#F6F1EA]" data-testid="tab-products">Prodotti</TabsTrigger>
          <TabsTrigger value="storico" className="rounded-full px-4 data-[state=active]:bg-[#121212] data-[state=active]:text-[#F6F1EA]" data-testid="tab-movements">Storico movimenti</TabsTrigger>
          <TabsTrigger value="ordini" className="rounded-full px-4 data-[state=active]:bg-[#121212] data-[state=active]:text-[#F6F1EA]" data-testid="tab-orders">Ordini</TabsTrigger>
        </TabsList>
        <TabsContent value="prodotti">
          <div className="mb-6 flex flex-wrap items-center gap-3">
            <div className="relative w-full max-w-xs">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9E958A]" />
              <input className={`${inputCls} pl-9 bg-white`} placeholder="Cerca prodotto" value={q} onChange={(e) => setQ(e.target.value)} data-testid="products-search-input" />
            </div>
            <button onClick={() => setOnlyLow(!onlyLow)} data-testid="filter-low-stock" className={`flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm transition-colors ${onlyLow ? "border-[#D97706] bg-amber-50 text-[#D97706]" : "border-[#E8E1D7] bg-white text-[#6E675F]"}`}>
              <AlertTriangle className="h-3.5 w-3.5" /> In esaurimento ({lowCount})
            </button>
          </div>
          {shown.length === 0 && <Empty testId="products-empty">Nessun prodotto. Aggiungi il primo con la sua foto.</Empty>}
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {shown.map((p) => <ProductCard key={p.id} p={p} onEdit={() => setPDlg({ id: p.id, f: { ...p } })} onMove={(type) => setMDlg({ p, type })} onHistory={() => openHistory(p)} onDelete={() => del(p)} />)}
          </div>
        </TabsContent>
        <TabsContent value="storico"><Panel className="p-4 sm:p-6"><MovementList items={movements} /></Panel></TabsContent>
        <TabsContent value="ordini"><OrdersList orders={orders} reload={load} /></TabsContent>
      </Tabs>
      <OrderDialog open={orderOpen} onOpenChange={setOrderOpen} products={products} onCreated={load} />
      <ProductDialog state={pDlg} setState={setPDlg} onSaved={load} />
      <MovementDialog state={mDlg} setState={setMDlg} onSaved={load} />
      <Dialog open={!!hist} onOpenChange={(o) => !o && setHist(null)}>
        <DialogContent className="bg-[#FDFBF7] border-[#E8E1D7] max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="product-history-dialog">
          <DialogHeader><DialogTitle className="font-serif text-3xl font-normal">Storico · {hist?.p.name}</DialogTitle></DialogHeader>
          {hist && <MovementList items={hist.items} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
